import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CANVAS_DEFAULT_HEIGHT_PX,
  CANVAS_DEFAULT_WIDTH_PX,
  ClearCommand,
  LoadCommand,
  RedoCommand,
  UndoCommand
} from './textbook-drawer-commands';
import { TextbookDrawerCommand } from '../public-api';
import { ToolName } from './tools';

@Injectable()
export class TextbookDrawerService implements OnDestroy {
  private readonly color$ubject = new BehaviorSubject('#000000');
  private readonly size$ubject = new BehaviorSubject(10);
  private readonly toolName$ubject = new BehaviorSubject<ToolName|undefined>(undefined);
  private readonly command$subject = new Subject<TextbookDrawerCommand>();
  private readonly undoLength$ubject = new BehaviorSubject(0);
  private readonly redoLength$ubject = new BehaviorSubject(0);
  private readonly currentState$ubject = new BehaviorSubject('');
  private readonly isLoading$ubject = new BehaviorSubject(false);
  private readonly isLocked$ubject = new BehaviorSubject(false);
  private readonly savedState$ubject = new BehaviorSubject<string>('');
  private readonly emptyDrawing = this.buildEmptyDrawing();
  private ctx: CanvasRenderingContext2D|undefined;

  readonly stateEdited$ = combineLatest([
    this.savedState$ubject.asObservable(),
    this.currentState$ubject.asObservable(),
  ]).pipe(
    map(([savedState, currentState]) => !this.isEqual(savedState, currentState))
  );

  isStateEdited(): boolean {
    return !this.isEqual(this.savedState$ubject.getValue(), this.currentState$ubject.getValue());
  }

  get color$(): Observable<string> {
    return this.color$ubject.asObservable();
  }

  get size$(): Observable<number> {
    return this.size$ubject.asObservable();
  }

  get toolName$(): Observable<ToolName | undefined> {
    return this.toolName$ubject.asObservable();
  }

  get command$(): Observable<TextbookDrawerCommand> {
    return this.command$subject.asObservable();
  }

  /**
   * Amount of stored undo states. Can be used to block undo button.
   */
  get undoLength$(): Observable<number> {
    return this.undoLength$ubject.asObservable();
  }

  /**
   * Amount of stored redo states. Can be used to block redo button.
   */
  get redoLength$(): Observable<number> {
    return this.redoLength$ubject.asObservable();
  }

  get currentState$(): Observable<string> {
    return this.currentState$ubject.asObservable();
  }

  /**
   * currently displayed dataUrl of canvas image
   */
  get currentState(): string | undefined {
    return this.currentState$ubject.getValue() || undefined;
  }

  /**
   * returns true if canvas is empty from any drawings
   */
  get isCanvasClear$(): Observable<boolean> {
    return this.currentState$.pipe(
      map((state) => {
        if (!state) {
          return true;
        }
        if (!this.ctx) {
          throw new Error('Canvas not set');
        }
        return this.haveCanvasNonZeroValues(this.ctx);
      })
    );
  }

  /**
   * returns true if canvas is empty from any drawings
   */
  get isCanvasClear(): boolean {
    if (!this.currentState) {
      return true;
    }
    if (!this.ctx) {
      throw new Error('Canvas not set');
    }
    return this.haveCanvasNonZeroValues(this.ctx);
  }

  /**
   * Truthy when new canvas image is loading
   */
  get isLoading$(): Observable<boolean> {
    return this.isLoading$ubject.asObservable();
  }

  get isLocked$(): Observable<boolean> {
    return this.isLocked$ubject.asObservable();
  }

  setColor(color: string): void {
    this.color$ubject.next(color);
  }

  getColor(): string {
    return this.color$ubject.getValue();
  }

  setSize(size: number): void {
    this.size$ubject.next(size);
  }

  getSize(): number {
    return this.size$ubject.getValue();
  }

  setToolName(tool: ToolName | undefined): void {
    this.toolName$ubject.next(tool);
  }

  getToolName(): ToolName | undefined {
    return this.toolName$ubject.getValue();
  }

  /**
   * Clears entire main canvas content.
   */
  clear(): void {
    this.command$subject.next(new ClearCommand());
  }
  /**
   * Load new state of main canvas from dataUrl.
   * @param state DataUrl of image. Example: `data:image/png;base64,iVBORw0KGgoAA...`
   */
  load(state: string): void {
    this.command$subject.next(new LoadCommand(state));
  }

  /**
   * Move main canvas in time one step backward.
   */
  undo(): void {
    this.command$subject.next(new UndoCommand());
  }

  /**
   * Move main canvas in time one step forward. Works only if canvas has been undone before.
   */
  redo(): void {
    this.command$subject.next(new RedoCommand());
  }

  /** Internal method. Usage not recommended */
  _setUndoLength(length: number): void {
    this.undoLength$ubject.next(length);
  }

  setLocked(isLocked: boolean): void {
    return this.isLocked$ubject.next(isLocked);
  }

  // PACKAGE INTERNALS

  /** Internal method. Usage not recommended */
  _setRedoLength(length: number): void {
    this.redoLength$ubject.next(length);
  }

  /** Internal method. Usage not recommended */
  _setCurrentState(state: string): void {
    this.currentState$ubject.next(state);
  }

  /** Internal method. Usage not recommended */
  _setIsLoading(isLoading: boolean): void {
    this.isLoading$ubject.next(isLoading);
  }

  /** Internal method. Usage not recommended */
  _setMainCanvas(canvas: HTMLCanvasElement): void {
    this.ctx = canvas?.getContext('2d') || undefined;
  }

  setSavedImageDataUrl(imageDataUrl: string): void {
    this.savedState$ubject.next(imageDataUrl);
  }

  ngOnDestroy(): void {
    this.color$ubject.complete();
    this.size$ubject.complete();
    this.toolName$ubject.complete();
    this.command$subject.complete();
    this.undoLength$ubject.complete();
    this.redoLength$ubject.complete();
    this.currentState$ubject.complete();
    this.savedState$ubject.complete();
    this.isLoading$ubject.complete();
    this.isLocked$ubject.complete();
  }

  private haveCanvasNonZeroValues(ctx: CanvasRenderingContext2D): boolean {
    const { width, height } = ctx.canvas;
    const imageBuffer = ctx.getImageData(0, 0, width, height).data.buffer;
    return !new Uint32Array(imageBuffer).some((x) => x !== 0);
  }

  private isEqual(savedState: string, currentState: string): boolean {
    if (this.isEmptyDrawing(savedState)) {
      return this.isEmptyDrawing(currentState);
    } else if (this.isEmptyDrawing(currentState)) {
      return this.isEmptyDrawing(savedState);
    }
    return savedState.length === currentState.length && savedState === currentState;
  }

  private isEmptyDrawing(state: string): boolean {
    return !state || (state.length === this.emptyDrawing.length && state === this.emptyDrawing);
  }

  private buildEmptyDrawing(): string {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_DEFAULT_WIDTH_PX;
    canvas.height = CANVAS_DEFAULT_HEIGHT_PX;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to generate empty canvas drawing');
    }
    const base64Image = canvas.toDataURL('image/png');
    return base64Image;
  }
}
